import classNames from 'classnames';
import { format, set, startOfDay } from 'date-fns';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { chartMonthDateRangeOptions } from '@/modules/common/constants/chart-date-range-oprions.ts';
import { useGetRegisterIdParam } from '@/modules/common/hooks/useGetRegisterIdParam.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { findDefaultValue } from '@/modules/common/utils/default-value.ts';
import { useIssuerGetIssuedCapitalOverTimeQuery } from '@/modules/issuer-dashboard/queries/charts.ts';
import { useIssuerGetIssuedCapitalSummaryQuery } from '@/modules/issuer-dashboard/queries/dashboard.ts';
import { useIssuerRegisterClassListQuery } from '@/modules/issuer-dashboard/queries/registers.tsx';
import { AnimatedNumbersSet, BalanceChartCanvas, InfoCard, Select, SelectButtons, Spinner } from 'ui';

const RANGE_QUERY_KEY = 'cs_date_range';
const CLASS_QUERY_KEY = 'cs_class';

const CapitalSummary = () => {
  // to fix bug with displaying 00000 instead of real number on firefox
  const [isChangingClassParam, setIsChangingClassParam] = useState(false);
  const [params, setParams] = useSearchParams();
  const getRegisterTypeStrings = useRegisterTypeStrings();

  const dateRangeQueryParam = params.get(RANGE_QUERY_KEY);
  const registerClassQueryParam = params.get(CLASS_QUERY_KEY);

  const registerClass =
    registerClassQueryParam && !isNaN(+registerClassQueryParam) ? +registerClassQueryParam : 0;
  const registerParam = useGetRegisterIdParam();
  const registerId = registerParam && !isNaN(+registerParam) ? +registerParam : 0;

  const selectedDateRangeOption =
    findDefaultValue(chartMonthDateRangeOptions, dateRangeQueryParam) ||
    chartMonthDateRangeOptions[1];

  const { data: summaryData, isFetching: isSummaryFetching } =
    useIssuerGetIssuedCapitalSummaryQuery({
      register_class_id: registerClass || undefined,
      date_to: dayjs().format('YYYY-MM-DDTHH:mm'),
      date_from: selectedDateRangeOption.value
        ? dayjs().subtract(+selectedDateRangeOption.value, 'days').format('YYYY-MM-DDTHH:mm')
        : undefined,
    });

  const { data: capitalOverTimeData, isFetching: isCapitalOverTimeLoading } =
    useIssuerGetIssuedCapitalOverTimeQuery({
      register_class_id: registerClass || undefined,
      date_to: dayjs().format('YYYY-MM-DDTHH:mm'),
      date_from: selectedDateRangeOption.value
        ? dayjs().subtract(+selectedDateRangeOption.value, 'days').format('YYYY-MM-DDTHH:mm')
        : undefined,
    });

  const { data: classesData, isLoading: isClassesLoading } = useIssuerRegisterClassListQuery(
    {},
    registerId,
  );

  const handleChangeDateRange = useCallback(
    (option: string) => {
      if (!option) params.delete(RANGE_QUERY_KEY);
      else params.set(RANGE_QUERY_KEY, option);
      setParams(params, { replace: false });
    },
    [params, setParams],
  );

  const handleChangeClass = useCallback(
    (option: number | null) => {
      if (option === registerClass) return;
      if (option) params.set(CLASS_QUERY_KEY, option.toString());
      else params.delete(CLASS_QUERY_KEY);
      setParams(params);
      setIsChangingClassParam(true);
      setTimeout(setIsChangingClassParam, 200, false);
    },
    [params, registerClass, setParams],
  );

  const classesOptions = useMemo(
    () => [
      { label: 'All classes', value: 0 },
      ...(classesData?.registers || []).map(({ id, class_name }) => ({
        label: class_name,
        value: id,
      })),
    ],
    [classesData?.registers],
  );

  const statistics = useMemo(() => {
    let res = (summaryData?.data || Array(5).fill({}))
      .filter(({ type }) => type !== 'Unit shares')
      .map(({ type, value }) => ({
        number: value || 0,
        title: `${type}`.replace(
          'Applications',
          getRegisterTypeStrings('application', { plural: true, capitalize: true }),
        ),
        prefix: '$',
      }));

    if (!registerClass) res = res.filter(({ title }) => title !== 'Current NAV');

    return res;
  }, [getRegisterTypeStrings, registerClass, summaryData?.data]);

  const capitalOverTimeChartData = useMemo(() => {
    const data = capitalOverTimeData?.data || [];
    if (data.length === 1) {
      const now = new Date();
      data[0] = {
        ...data[0],
        date: set(new Date(data[0].date), {
          hours: now.getHours(),
          minutes: now.getMinutes(),
          seconds: now.getSeconds(),
          milliseconds: now.getMilliseconds(),
        }).toString(),
      };

      data.unshift({
        value: data[0].value,
        date: startOfDay(new Date(data[0].date)).toString(),
      });
    }

    return [
      {
        name: '',
        data: data.map(({ date, value }) => {
          const isMidnightUTC =
            format(new Date(date), 'H:m:s') === '0:0:0' ||
            format(new Date(date), 'H:m:s') === '3:0:0' ||
            format(new Date(date), 'H:m:s') === '2:0:0';

          const formattedDate = isMidnightUTC
            ? format(new Date(date), 'dd-MM-yyyy')
            : format(new Date(date), 'dd-MM-yyyy HH:mm:ss');
          return {
            x: formattedDate,
            y: value,
          };
        }),
      },
    ];
  }, [capitalOverTimeData?.data]);

  useEffect(() => {
    const registerClassObj = (classesData?.registers || []).find(({ id }) => id === registerClass);

    if (classesData?.registers && !registerClassObj && registerClass) {
      handleChangeClass(null);
    }
  }, [classesData, handleChangeClass, registerClass, registerId]);

  const isCapitalOverTimeChartData =
    !!capitalOverTimeChartData[0].data.length &&
    capitalOverTimeChartData[0].data.some(({ y }) => !!y);

  return (
    <InfoCard
      title="Capital Summary"
      headerAction={
        <Select
          label=""
          withBorder
          isClearable={!!registerClass}
          placeholder="All classes"
          classNameLabel="!mb-0"
          className="w-auto min-w-[180px] h-10 z-[2]"
          onChange={handleChangeClass}
          value={registerClass}
          options={classesOptions}
          isLoading={isClassesLoading}
        />
      }
    >
      <div className="space-y-4">
        <AnimatedNumbersSet
          numbers={statistics}
          loading={isSummaryFetching || isChangingClassParam}
          classes={{
            container: classNames('border-none !p-0 !grid-cols-1 md:!grid-cols-2', {
              'lg:!grid-cols-5 [&>div:not(:last-child)>div]:border-0': !registerClass,
              'lg:!grid-cols-5 [&>div:not(:nth-last-child(2))>div]:border-0': registerClass,
            }),
            item: '!gap-0',
            itemTitle: 'text-xs min-w-[100px]',
            itemNumber: '!text-lg md:!text-lg min-w-[130px]',
            itemContainer: 'flex',
          }}
        />
        <div className="flex justify-between items-center">
          <div className="text-sm font-medium">Issued capital over time</div>
          <SelectButtons<string>
            options={chartMonthDateRangeOptions}
            selected={selectedDateRangeOption}
            onChange={handleChangeDateRange}
            className="w-[175px]"
            disabled={isCapitalOverTimeLoading}
          />
        </div>
        {isCapitalOverTimeLoading && (
          <div className="w-full h-[222px] flex items-center justify-center">
            <Spinner size="lg" />
          </div>
        )}
        {!isCapitalOverTimeLoading && !isCapitalOverTimeChartData && (
          <div className="h-[300px] flex justify-center items-center font-medium text-lg">
            No data for this period
          </div>
        )}
        {!isCapitalOverTimeLoading && isCapitalOverTimeChartData && (
          <BalanceChartCanvas
            fill={false}
            withDateTime={true}
            height="300px"
            type="line"
            data={capitalOverTimeChartData}
            markerStrokeColor="#FFFFFF"
          />
        )}
      </div>
    </InfoCard>
  );
};

export default CapitalSummary;
